'use client';

import Image from 'next/image';

import { ArrowUpRight } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleAutopilot,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { Button } from '@/components/ui/button';
import { LinkPreview } from '@/components/ui/link-preview';
import { MagicalText } from '@/components/ui/magical-text';
import { useTranslation } from '@/lib/i18n/context';

const ObservabilitySection = () => {
  const { t } = useTranslation();

  const features = [
    {
      id: 1,
      title: t.agent5['Every Decision Explained'],
      description:
        t.agent5[
          'See why Agent 5 made each decision. Full reasoning traces, confidence scores, and source citations for every response.'
        ],
    },
    {
      id: 2,
      title: t.agent5['Real-time Performance Metrics'],
      description:
        t.agent5[
          'Track resolution rates, accuracy, response times, and customer satisfaction in real-time. Know exactly how your AI is performing.'
        ],
    },
    {
      id: 3,
      title: t.agent5['Conversation Replays'],
      description:
        t.agent5[
          'Review any conversation in detail. See what Agent 5 saw, understood, and why it responded the way it did.'
        ],
    },
    {
      id: 4,
      title: t.agent5['Error Detection & Alerts'],
      description:
        t.agent5[
          'Automatic detection of mistakes, edge cases, and areas for improvement. Get alerts when Agent 5 needs help.'
        ],
    },
    {
      id: 5,
      title: t.agent5['Evaluation Dashboard'],
      description:
        t.agent5[
          'Built-in evaluation tools show you where Agent 5 excels and where it needs training. Data-driven improvement, not guesswork.'
        ],
    },
  ];

  return (
    <section className="bg-[rgb(11,21,25)] py-32 text-white">
      <div className="container mx-auto">
        <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center">
          <h2 className="font-serif text-2xl text-white md:text-4xl">
            {t.agent5['AI you can actually understand']}
          </h2>
          <p className="text-sm text-white/80 md:text-base">
            {
              t.agent5[
                'Most AI is a black box. Agent 5 shows you everything—every decision, every confidence score, every mistake. Observability at scale.'
              ]
            }
          </p>
        </div>
        <div className="mb-32 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20 md:p-7"
            >
              <h3 className="font-serif text-lg text-white md:text-xl">
                {feature.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Section */}
      <div id="compliance" className="container">
        <div className="mb-32 flex flex-col gap-16">
          <div className="mx-auto grid w-full max-w-3xl grid-cols-2 place-items-center gap-16 md:grid-cols-3">
            <div className="flex flex-col items-center gap-3">
              <Image
                src="https://deifkwefumgah.cloudfront.net/shadcnblocks/block/compliance/AICPA-SOC.svg"
                alt="AICPA SOC 2"
                width={120}
                height={120}
                className="max-h-32 invert"
              />
              <span className="rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1 text-xs text-green-400">
                {t.agent5['Certified']}
              </span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <Image
                src="https://deifkwefumgah.cloudfront.net/shadcnblocks/block/compliance/ISO-27001.svg"
                alt="ISO 27001"
                width={120}
                height={120}
                className="max-h-32 invert"
              />
              <span className="rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1 text-xs text-green-400">
                {t.agent5['Certified']}
              </span>
            </div>

            <div className="flex flex-col items-center gap-3">
              <Image
                src="https://deifkwefumgah.cloudfront.net/shadcnblocks/block/compliance/GDPR.svg"
                alt="GDPR"
                width={120}
                height={120}
                className="max-h-32 invert"
              />
              <span className="rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1 text-xs text-green-400">
                {t.agent5['Live']}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function LocalizedAgent5Page() {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <>
      <SubpageHero
        eyebrow={isAr ? 'المنتج' : 'Product'}
        title={
          isAr ? (
            <>
              <MagicalText>Agent 5</MagicalText> — العقل الذكي وراء كل حل
            </>
          ) : (
            <>
              <MagicalText>Agent 5</MagicalText> — the AI brain behind every
              resolution
            </>
          )
        }
        subtitle={
          isAr ? (
            <>
              درّب مرة، شغّل في كل مكان — دردشة، إيميل، مكالمات، ومنصات التواصل.
              <br />
              ٧٧٪ أتمتة. إجراءات متعددة الخطوات تحل فعلاً.
            </>
          ) : (
            <>
              Train once, deploy everywhere — chat, email, voice, social.
              <br />
              77% automation. Multi-step actions that actually solve.
            </>
          )
        }
        videoSrc="/home/features/cookinglife.mp4"
        worksWith={null}
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleAutopilot
                title={
                  isAr
                    ? 'Agent 5 · تفكير متعدد الخطوات'
                    : 'Agent 5 · multi-step reasoning'
                }
                steps={
                  isAr
                    ? [
                        'رصدنا: استرداد + إعادة جدولة',
                        'سحبنا الطلب #4521 من Shopify',
                        'تحققنا من الناقل · ما زال في الطريق',
                        // U+2066 LRI / U+2069 PDI keep "$89.00" as one LTR run;
                        // without them bidi pushes the "$" to the right of the digits.
                        'صرفنا الاسترداد · ⁦$89.00⁩',
                        'حجزنا توصيل جديد · الجمعة ١٧ مايو',
                        'أكّدنا عبر SMS + الإيميل',
                      ]
                    : [
                        'Detected: refund + reschedule',
                        'Pulled order #4521 from Shopify',
                        'Checked carrier · still in transit',
                        'Issued refund · $89.00',
                        'Booked new delivery · Fri May 17',
                        'Confirmed via SMS + email',
                      ]
                }
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <div id="training">
        <BenefitsSection
          title={t.agent5['Agent 5 Capabilities']}
          subtitle={t.agent5['The AI engine behind everything']}
          learnMoreLink="#evals"
          accentColor="emerald"
          theme="light"
          benefits={[
            {
              title: t.agent5['Train Once, Deploy Everywhere'],
              description:
                t.agent5[
                  'Train your AI once and deploy it across Voice, Chat, Email, WhatsApp, Social, and Slack. One training session, unlimited channels.'
                ],
              linkText: '',
              linkHref: '#',
              videoUrl: '/product/agent5/train-once-deploy-everywhere.mp4',
              imagePosition: 'right',
            },
            {
              title: t.agent5['77% Average Automation Rate'],
              description:
                t.agent5[
                  'State-of-the-art AI performance. Market-leading automation rate that resolves the majority of customer inquiries automatically.'
                ],
              linkText: '',
              linkHref: '#',
              videoUrl: '/product/agent5/77-average-automation-rate.mp4',
              imagePosition: 'left',
            },
            {
              title: t.agent5['Multi-step Actions'],
              description:
                t.agent5[
                  'Agent 5 takes multiple actions to solve complex problems. Not just answering questions—actually resolving issues through API calls and workflows.'
                ],
              linkText: '',
              linkHref: '#',
              videoUrl: '/product/agent5/multi-step-actions.mp4',
              imagePosition: 'right',
            },
            {
              title: t.agent5['95%+ Accuracy'],
              description:
                t.agent5[
                  'Maintains over 95% accuracy in responses. High precision, low error rate, reliable answers customers can trust.'
                ],
              linkText: '',
              linkHref: '#',
              imageUrl:
                'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-4.svg',
              imagePosition: 'left',
            },
          ]}
        />
      </div>

      {/* Authentic Message Section */}
      <section className="mb-6 overflow-hidden py-32">
        <div className="container w-full">
          <h2 className="text-muted-foreground/40 mt-10 text-center font-serif text-3xl md:text-4xl">
            {t.agent5['Most AI agents are']}{' '}
            <LinkPreview
              url="https://en.wikipedia.org/wiki/Chatbot"
              className="px-1"
            >
              {t.agent5['chatbots']}
            </LinkPreview>{' '}
            {
              t.agent5[
                'with better marketing. They answer FAQs, follow scripts, break on edge cases.'
              ]
            }
            <br />
            <br />
            {t.agent5['Agent 5 is different. It understands']}{' '}
            <LinkPreview
              url="https://en.wikipedia.org/wiki/Context_(language_use)"
              className="px-1"
            >
              {t.agent5['context']}
            </LinkPreview>
            {t.agent5[', learns from mistakes, takes']}{' '}
            <LinkPreview
              url="https://en.wikipedia.org/wiki/Workflow"
              className="px-1"
            >
              {t.agent5['actions']}
            </LinkPreview>
            {t.agent5['. Built with']}{' '}
            <LinkPreview
              url="https://en.wikipedia.org/wiki/Observability"
              className="px-1"
            >
              {t.agent5['observability']}
            </LinkPreview>{' '}
            {t.agent5['and']}
            {/* Arabic waw al-atf attaches to the next word: "والامتثال", not "و الامتثال". */}
            {isAr ? '' : ' '}
            <LinkPreview
              url="https://en.wikipedia.org/wiki/Regulatory_compliance"
              className={isAr ? 'ltr:px-1 rtl:pl-1' : 'px-1'}
            >
              {t.agent5['compliance']}
            </LinkPreview>{' '}
            {t.agent5['from day one.']}
            <br />
            <br />
            {
              t.agent5[
                "We're not selling magic. We're building reliable AI that actually works—the kind you'd trust with your customers."
              ]
            }
          </h2>
        </div>
      </section>

      {/* Observability Section - Dark */}
      <div id="observability">
        <ObservabilitySection />
      </div>

      {/* API Availability Section */}
      <section id="actions" className="bg-white py-32">
        <div className="container">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="mb-4 font-serif text-2xl md:text-4xl">
              {t.agent5['Complete API access']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.agent5[
                  'Build custom integrations, automate workflows, and extend Agent 5 with full API access. RESTful endpoints for every feature.'
                ]
              }
            </p>
          </div>
          <div className="mx-auto max-w-4xl space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">{t.agent5['Chat Sessions']}</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.agent5[
                      'Create, manage, and monitor chat sessions across all channels'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /chat-sessions
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">{t.agent5['AI Phone Agents']}</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.agent5[
                      'Create and manage AI phone agents, make calls programmatically'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /ai-phone-agents
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">{t.agent5['Email Automation']}</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.agent5[
                      'Send emails, manage custom domains, verify DNS settings'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /emails
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">{t.agent5['WhatsApp']}</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t.agent5['Send WhatsApp templates and messages via API']}
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /whatsapp/template
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">{t.agent5['AI Training']}</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.agent5[
                      'Create custom training, generate scenarios from chat history'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /training
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.agent5['Outbound Sequences']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.agent5[
                      'Create, manage, and execute outbound messaging sequences'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /sequences
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.agent5['Customer Insights']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.agent5[
                      'Get AI-powered insights, resolve and assign to teams'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  GET /insights
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">{t.agent5['CSAT & Feedback']}</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t.agent5['Track customer satisfaction scores and feedback']}
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  GET /csat
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">{t.agent5['Webhooks']}</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.agent5[
                      'Receive real-time events for all Agent 5 activities'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /webhooks
                </code>
              </div>
            </div>
            <div className="flex justify-center pt-8">
              <Button size="lg" asChild>
                <a
                  href="https://docs.open.cx/api-reference"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.agent5['View API Documentation']}{' '}
                  <ArrowUpRight className="size-4 ltr:ml-2 rtl:mr-2" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        id="evals"
        title={t.agent5['Frequently asked questions']}
        subtitle={t.agent5['Find answers to common questions about Agent 5.']}
        items={[
          {
            question: t.agent5['What is Agent 5?'],
            answer:
              t.agent5[
                "Agent 5 is the core AI engine that powers all customer communications. It's a multi-model AI with training, observability, compliance, and evaluation capabilities."
              ],
          },
          {
            question: t.agent5['How does train once, deploy everywhere work?'],
            answer:
              t.agent5[
                'Train Agent 5 with your knowledge base once. It automatically works across all channels—Voice, Chat, Email, WhatsApp, Social, and Slack—with consistent responses.'
              ],
          },
          {
            question: t.agent5['What does 77% automation rate mean?'],
            answer:
              t.agent5[
                '77% of customer queries are fully resolved by Agent 5 without human intervention. This is the average across all customers and represents industry-leading performance.'
              ],
          },
          {
            question:
              t.agent5['Can Agent 5 take actions, not just answer questions?'],
            answer:
              t.agent5[
                'Yes. Agent 5 can execute multi-step workflows, make API calls, update systems, and perform actions to actually solve customer issues.'
              ],
          },
          {
            question: t.agent5['How accurate is Agent 5?'],
            answer:
              t.agent5[
                'Agent 5 maintains over 95% accuracy in responses. This is measured through evaluation metrics and customer feedback.'
              ],
          },
          {
            question:
              t.agent5['What makes Agent 5 different from other AI agents?'],
            answer:
              t.agent5[
                'Built-in training tools, full observability, compliance features, continuous learning, and evaluation capabilities. Not just an AI—a complete AI management system.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
